import { isNumber, isNumeric } from '@cutwise/utils';

import { B2BModel } from '@/b2b/entity/B2B';
import { ClientSetting } from '@/common/constants/clientSettings';
import { Permission } from '@/common/constants/permissions';
import { Rollout } from '@/common/constants/rollouts';
import { S3UploadResponse } from '@/common/types/s3UploadResponse';
import { StoryModalState } from '@/gasolineMovies/types/StoryModalState';
import { MODEL_CONVERSION_PRICE } from '@/spectrum/constants/conversionPrice';

/** @deprecated Use UserV2Model for most cases, since User is deprecated on backend */
export class UserModel {
  id?: Nullable<number>;

  /** What the user may do, permanently. */
  permissions!: string[];

  /** Features still being shipped that are open to this user, taken away once the feature is out. */
  rollouts!: string[];

  /** How the system behaves for the user and their company, rather than what they may do. */
  enabledSettings!: string[];

  /** Whether the admin opens at all. The areas behind it stay there and decide what it shows. */
  canAccessAdmin!: boolean;

  /** Whether the admin lets the user export a copy of a product or project to another account or environment. */
  canExportCopy!: boolean;
  b2b!: B2BModel;
  lastName!: Nullable<string>;
  firstName!: Nullable<string>;
  username!: string;
  email!: string;
  phone!: Nullable<string>;
  utcTimezone!: string;
  locale!: string;

  /** The user's own stored preferences, unrelated to enabledSettings. */
  settings!: Nullable<
    Partial<{
      storyBackgrounds: S3UploadResponse[];
      storyModalState: Partial<StoryModalState>;
    }>
  >;
}

/** @deprecated Use UserV2 for most cases, since User is deprecated on backend */
export class User extends UserModel {
  constructor(modelData: Partial<UserModel> = {}) {
    super();

    Object.assign(this, modelData);

    this.settings = modelData?.settings || {};
  }

  get isAnonymous() {
    return !this.id;
  }

  get b2bId() {
    return this.b2b?.id ?? null;
  }

  get b2bTitle() {
    return this.b2b?.title ?? null;
  }

  get currency() {
    return this.b2b?.account?.currency ?? null;
  }

  get overdraft() {
    const overdraft = this.b2b?.account?.overdraft;

    return isNumeric(overdraft) ? +overdraft : null;
  }

  get balance() {
    const balance = this.b2b?.account?.balance;

    return isNumeric(balance) ? +balance : null;
  }

  get canAffordModelConversion() {
    return this.canAfford(MODEL_CONVERSION_PRICE);
  }

  get getFullName() {
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }

    if (this.firstName) {
      return this.firstName;
    }

    if (this.lastName) {
      return this.lastName;
    }

    return '';
  }

  get getUserRenderName() {
    const userFullName = this.getFullName;

    if (userFullName) {
      return userFullName;
    }

    if (this.username) {
      return this.username;
    }

    return 'Unknown user';
  }

  hasPermission(permission: Permission) {
    return Boolean(this.permissions?.includes(permission));
  }

  hasRollout(rollout: Rollout) {
    return Boolean(this.rollouts?.includes(rollout));
  }

  hasSetting(setting: ClientSetting) {
    return Boolean(this.enabledSettings?.includes(setting));
  }

  updateSettings<K extends keyof NonNullable<UserModel['settings']>>(
    field: K,
    value: NonNullable<UserModel['settings']>[K],
  ) {
    const prevSettings = this.settings || {};

    this.settings = {
      ...prevSettings,
      [field]: value,
    };
  }

  canAfford(amount: number) {
    if (amount <= 0) {
      return true;
    }

    const balance = this.balance;
    const overdraft = this.overdraft;

    const isBalanceEnough = isNumber(balance) && balance >= amount;
    const isOverdraftEnough = isNumber(overdraft) && overdraft >= amount;

    return isBalanceEnough || isOverdraftEnough;
  }
}
